/**
 * Report Utilities and Helpers
 */

/**
 * Bucket items by date with specified granularity
 * @param {Array} items - Array of items to bucket
 * @param {string} dateField - Field name containing the date
 * @param {string} granularity - 'day', 'week', or 'month'
 * @returns {Object} Bucketed data with dates as keys
 */
export function bucketByDate(items, dateField = 'created_at', granularity = 'day') {
  const buckets = {}
  
  items.forEach(item => {
    const date = new Date(item[dateField])
    if (isNaN(date.getTime())) return // Skip invalid dates
    
    let bucketKey
    
    switch (granularity) {
      case 'day':
        bucketKey = date.toISOString().split('T')[0] // YYYY-MM-DD
        break
      case 'week':
        // Get Monday of the week
        const weekStart = new Date(date)
        weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1))
        bucketKey = weekStart.toISOString().split('T')[0]
        break
      case 'month':
        bucketKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` // YYYY-MM
        break
      default:
        bucketKey = date.toISOString().split('T')[0]
    }
    
    if (!buckets[bucketKey]) {
      buckets[bucketKey] = []
    }
    buckets[bucketKey].push(item)
  })
  
  return buckets
}

/**
 * Build query parameters from filters
 * @param {Object} params - Filter parameters
 * @returns {URLSearchParams} Query string parameters
 */
export function buildQuery(params) {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v))
      } else {
        searchParams.append(key, value)
      }
    }
  })
  
  return searchParams
}

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file
 * @param {Array} headers - Optional custom headers
 */
export function downloadCsv(data, filename = 'export.csv', headers = null) {
  if (!data || data.length === 0) {
    console.warn('No data to export')
    return
  }
  
  // Get headers
  const csvHeaders = headers || Object.keys(data[0])
  
  // Build CSV content
  const csvRows = [
    csvHeaders.join(','), // Header row
    ...data.map(row => 
      csvHeaders.map(header => {
        const value = row[header]
        // Handle values with commas, quotes, or newlines
        if (value === null || value === undefined) return ''
        const stringValue = String(value)
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      }).join(',')
    )
  ]
  
  const csvContent = '\uFEFF' + csvRows.join('\n') // UTF-8 BOM for Excel compatibility
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  
  link.setAttribute('href', url)
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`)
  link.style.visibility = 'hidden'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  URL.revokeObjectURL(url)
}

/**
 * Calculate date range presets
 * @param {string} preset - Preset name
 * @returns {Object} Object with startDate and endDate
 */
export function getDateRangePreset(preset) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let startDate, endDate
  
  switch (preset) {
    case 'last7days':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 7)
      endDate = today
      break
    case 'last30days':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 30)
      endDate = today
      break
    case 'last90days':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 90)
      endDate = today
      break
    case 'thisQuarter':
      const currentQuarter = Math.floor(now.getMonth() / 3)
      startDate = new Date(now.getFullYear(), currentQuarter * 3, 1)
      endDate = today
      break
    case 'ytd':
      startDate = new Date(now.getFullYear(), 0, 1)
      endDate = today
      break
    default:
      return { startDate: null, endDate: null }
  }
  
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  }
}

/**
 * Calculate delta/change between current and previous period
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {Object} Object with delta and percentage
 */
export function calculateDelta(current, previous) {
  if (previous === 0) {
    return {
      delta: current,
      percentage: current > 0 ? 100 : 0,
      direction: current > 0 ? 'up' : 'neutral'
    }
  }
  
  const delta = current - previous
  const percentage = ((delta / previous) * 100).toFixed(1)
  
  return {
    delta,
    percentage: parseFloat(percentage),
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral'
  }
}

/**
 * Generate sparkline data from bucketed data
 * @param {Object} buckets - Bucketed data
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Array} Array of {date, count} objects
 */
export function generateSparklineData(buckets, startDate, endDate) {
  const data = []
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  // Fill all dates in range
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dateKey = d.toISOString().split('T')[0]
    data.push({
      date: dateKey,
      count: buckets[dateKey] ? buckets[dateKey].length : 0
    })
  }
  
  return data
}

/**
 * Group items by field and count
 * @param {Array} items - Array of items
 * @param {string} field - Field to group by
 * @returns {Object} Object with field values as keys and counts as values
 */
export function groupAndCount(items, field) {
  const groups = {}
  
  items.forEach(item => {
    const value = item[field] || 'Unknown'
    groups[value] = (groups[value] || 0) + 1
  })
  
  return groups
}

/**
 * Get top N values from grouped data
 * @param {Object} groups - Grouped data object
 * @param {number} n - Number of top items to return
 * @returns {Array} Array of {label, count} objects
 */
export function getTopN(groups, n = 10) {
  return Object.entries(groups)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n)
}

/**
 * Filter items by date range
 * @param {Array} items - Array of items
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} dateField - Field name containing the date
 * @returns {Array} Filtered items
 */
export function filterByDateRange(items, startDate, endDate, dateField = 'created_at') {
  if (!startDate && !endDate) return items
  
  const start = startDate ? new Date(startDate) : null
  const end = endDate ? new Date(endDate) : null
  
  return items.filter(item => {
    const itemDate = new Date(item[dateField])
    if (isNaN(itemDate.getTime())) return false
    
    if (start && itemDate < start) return false
    if (end && itemDate > end) return false
    
    return true
  })
}

/**
 * Calculate cumulative data for timeline
 * @param {Array} timelineData - Array of {date, count} objects
 * @returns {Array} Array with cumulative counts
 */
export function calculateCumulative(timelineData) {
  let cumulative = 0
  return timelineData.map(item => {
    cumulative += item.count
    return {
      ...item,
      cumulative
    }
  })
}

/**
 * Format large numbers with suffixes (K, M, B)
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export function formatLargeNumber(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B'
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

